import type { FC } from 'react'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { JsonLd } from 'react-schemaorg'
import { ClientOnly } from 'remix-utils/client-only'
import type { FAQPage } from 'schema-dts'
import { withBase } from 'ufo'
import IcBaselineArrowUpward from '~icons/ic/baseline-arrow-upward'
import IcOutlineAutoAwesome from '~icons/ic/outline-auto-awesome'
import IcOutlineBrush from '~icons/ic/outline-brush'
import IcOutlineGroups from '~icons/ic/outline-groups'
import IcOutlineRedeem from '~icons/ic/outline-redeem'
import Logo from 'src/components/Logo'
import mioChibiUrl from 'src/features/Tsubaki2Landing/assets/mio-chibi.webp'
import { DEFAULT_LOCALE } from 'src/i18n/constants'
import { useQs } from 'src/utils/qs'
import { useLocation } from 'src/utils/router/route'

const LazySign = lazy(() =>
  import('../components/Account/SignNew').then(m => ({
    default: m.default,
  })),
)

/** Sign-up page showcase — vibrant, diverse styles to inspire new users. */
const SHOWCASE_IMAGES_SIGNUP = [
  'https://images-ng.pixai.art/images/stillThumb/dc43f1e3-9e17-40d8-a6cf-1d5e43700402',
  'https://images-ng.pixai.art/images/stillThumb/f7e973a3-d397-4348-a2c4-8ea44a1c158c',
  'https://images-ng.pixai.art/images/stillThumb/d158ffa3-44aa-4521-ad54-5efb19103b99',
  'https://images-ng.pixai.art/images/stillThumb/6bff2998-603e-44dc-90c1-bfb44c7db4cc',
  'https://images-ng.pixai.art/images/stillThumb/720b8ab8-6793-48b8-a0aa-3d6d138397e2',
  'https://images-ng.pixai.art/images/stillThumb/e9f8e464-7ec7-4a78-ab23-b2f8378517c9',
  'https://images-ng.pixai.art/images/stillThumb/8169b555-59fe-44ef-933a-2eacb9bf6656',
  'https://images-ng.pixai.art/images/stillThumb/277eaed6-c23b-4aab-bbb9-af6043ea7cae',
  'https://images-ng.pixai.art/images/stillThumb/8211d5af-57f4-44d0-b7c0-a4d1fa23d582',
]

/** Login page showcase — curated Tsubaki-2 artworks showcasing AI quality. */
const SHOWCASE_IMAGES_LOGIN = [
  'https://images-ng.pixai.art/gi/stillThumb/8966204f-bfe1-4eb3-a880-0725597e3b1e',
  'https://images-ng.pixai.art/gi/stillThumb/f58ec0be-9e35-4faa-8212-176f33f9fc35',
  'https://images-ng.pixai.art/gi/stillThumb/603625d2-b768-4257-8cd8-925656760ccc',
  'https://images-ng.pixai.art/gi/stillThumb/f64e5f75-9e56-499e-9c63-0e2042c8f8fb',
  'https://images-ng.pixai.art/gi/stillThumb/da68a587-90b1-4101-baea-e9d4a1250842',
  'https://images-ng.pixai.art/gi/stillThumb/a7bac6af-0608-4e04-9798-a5559290ddc3',
  'https://images-ng.pixai.art/gi/stillThumb/9c4731b1-e733-4d2d-84e4-bcf16c2773a2',
  'https://images-ng.pixai.art/gi/stillThumb/5bb7db1a-291f-4a3a-8f54-473c731076e4',
  'https://images-ng.pixai.art/gi/stillThumb/0fa9f782-e0d7-4cfa-8d14-6fc19fe8f5f9',
]

const FEATURES = [
  {
    icon: IcOutlineAutoAwesome,
    key: 'ai-generation',
    href: '/generator/image',
  },
  { icon: IcOutlineBrush, key: 'models', href: '/market' },
  { icon: IcOutlineRedeem, key: 'free-credits', href: '/generator/image' },
  { icon: IcOutlineGroups, key: 'community', href: '/' },
] as const

const TOOLS = [
  { key: 'text-to-image', href: '/generator/image' },
  { key: 'image-to-image', href: '/generator/image/editor' },
  { key: 'ai-animation', href: '/generator/image/animation' },
  { key: 'realtime', href: '/generator/realtime' },
  { key: 'model-market', href: '/market' },
  { key: 'upscaler', href: '/generator/upscaler' },
] as const

const FAQ_KEYS_SIGNUP = [
  'free',
  'skills',
  'what',
  'commercial',
  'models',
] as const
const FAQ_KEYS_LOGIN = ['free', 'what', 'models'] as const

/**
 * Skeleton placeholder that **mirrors the real `<Sign compact>` layout**
 * in `components/Account/SignNew.tsx` block-for-block, so the
 * `<ClientOnly fallback={<FormSkeleton />}>` → real-form swap doesn't
 * shift any element vertically:
 *
 *   - Outer `pt-4` matches the wrapper around `tabSwitcher`.
 *   - Tab strip: `mx-6 mb-2 flex p-1 h-10` matches the real `tablist`.
 *   - Form body: `px-6 pt-1 pb-5 flex flex-col gap-3` matches `formContent`,
 *     and the 5 placeholder rows (Google button h-12 + 3-col secondary OAuth
 *     row h-11 + "or" divider + email button h-11 + 2-line TOS) match the
 *     5 children of the real `authForm`'s `flex flex-col gap-3`.
 *
 * The previous skeleton was a plain `flex flex-col gap-3 p-6` list with
 * no tab strip, h-10 (vs real h-12) Google button, h-10 (vs h-11) social
 * buttons, no "or" text, and a single 1-line TOS — that mismatched
 * compact `<Sign>` by ~20px in height *and* placed each element at a
 * different y offset, so hydrate visibly jolted the Mio mascot and the
 * "Trusted by 14M+ creators" line below the card.
 */
function FormSkeleton() {
  return (
    <div className="flex flex-col w-full pt-4 animate-pulse">
      {/* tabSwitcher placeholder */}
      <div className="mx-6 mb-2 flex h-10 rounded-lg bg-zinc-100 p-1 dark:bg-zinc-700/50" />
      {/* formContent placeholder */}
      <div className="flex flex-col gap-3 px-6 pt-1 pb-5">
        {/* Primary OAuth (Google) — real button is h-12 */}
        <div className="h-12 rounded-xl bg-zinc-100 dark:bg-zinc-700/50" />
        {/* Secondary OAuth row (Discord / Twitter / Apple) — real h-11 */}
        <div className="grid grid-cols-3 gap-2">
          <div className="h-11 rounded-xl bg-zinc-100 dark:bg-zinc-700/50" />
          <div className="h-11 rounded-xl bg-zinc-100 dark:bg-zinc-700/50" />
          <div className="h-11 rounded-xl bg-zinc-100 dark:bg-zinc-700/50" />
        </div>
        {/* "or" divider — real `<hr>/<span>or</span>/<hr>` flex row */}
        <div className="flex h-4 items-center gap-3">
          <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
          <div className="h-3 w-6 rounded bg-zinc-100 dark:bg-zinc-700/50" />
          <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
        </div>
        {/* Email continue button — real h-11 */}
        <div className="h-11 rounded-xl bg-zinc-100 dark:bg-zinc-700/50" />
        {/* TOS — real text-[11px] leading-relaxed wraps to ~2 lines */}
        <div className="mt-1 flex flex-col items-center gap-1.5">
          <div className="h-3 w-3/4 rounded bg-zinc-100 dark:bg-zinc-700/50" />
          <div className="h-3 w-1/2 rounded bg-zinc-100 dark:bg-zinc-700/50" />
        </div>
      </div>
    </div>
  )
}

/** Sticky CTA that appears when the form scrolls out of view. */
function StickyCtaButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  const [visible, setVisible] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      {/* Invisible sentinel placed right after the form */}
      <div ref={sentinelRef} className="h-0 w-0" aria-hidden="true" />
      {/* Floating button — bottom offset clears the reCAPTCHA badge
          (Google's badge is fixed bottom-right, ~74px tall incl. its own
          inset, so we sit at ~88px + safe-area to leave a clear gap). */}
      <button
        type="button"
        onClick={onClick}
        className={`fixed bottom-[calc(env(safe-area-inset-bottom,0px)+5.5rem)] right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full bg-purple-600 text-white text-sm font-medium shadow-lg hover:bg-purple-700 transition-all duration-300 cursor-pointer ${
          visible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-4 opacity-0 pointer-events-none'
        }`}
      >
        <IcBaselineArrowUpward className="size-4" />
        {label}
      </button>
    </>
  )
}

const SignPage: FC<{ isLogin?: boolean }> = ({ isLogin }) => {
  const { t } = useTranslation(['common', 'auth'])
  const [{ to, code }] = useQs()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ lang?: string }>()
  const lang = params.lang ?? DEFAULT_LOCALE
  const localePath = (href: string) => withBase(href, `/${lang}`)
  const formRef = useRef<HTMLDivElement>(null)

  // Force sign-up mode whenever `?to=` is present, even if its value is empty
  // (e.g. `/login?to=&code=ABC` — referral flow); a truthy check would treat
  // empty string as absent and incorrectly stay in login mode.
  const effectiveIsLogin = to !== undefined ? false : isLogin

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  return (
    // `<main>` (not `<div>`): /login and /sign-up are top-level routes and
    // don't sit under `<AppFrameShell>`, which is what wraps the body in
    // `<main>` for `_app.*` pages. Without an explicit `<main>` here the
    // SSR HTML has no main landmark, which trips screen readers and the
    // SSR validation checklist alike.
    <main className="min-h-screen overflow-x-clip bg-gradient-to-b from-purple-100/80 via-purple-50/40 to-zinc-50 dark:from-zinc-900 dark:via-zinc-950 dark:to-zinc-900">
      {/* Title/description are set by the SSR route loader (`server:meta.{login,register}.{title,description}`). */}
      <JsonLd<FAQPage>
        item={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: (effectiveIsLogin ? FAQ_KEYS_LOGIN : FAQ_KEYS_SIGNUP).map(
            key => ({
              '@type': 'Question',
              name: t(`auth:landing.faq.items.${key}.q`) ?? '',
              acceptedAnswer: {
                '@type': 'Answer',
                text: t(`auth:landing.faq.items.${key}.a`) ?? '',
              },
            }),
          ),
        }}
      />

      {/* ── Hero section — fills viewport, form is the visual center ── */}
      <div className="relative flex flex-col items-center justify-center px-5 sm:px-6 py-6 sm:py-8 lg:min-h-screen">
        {/* Decorative background */}
        <div
          className="pointer-events-none absolute inset-0 transform-gpu overflow-hidden"
          aria-hidden="true"
        >
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-purple-200/40 dark:bg-purple-900/20 blur-3xl" />
          <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] rounded-full bg-indigo-200/30 dark:bg-indigo-900/15 blur-3xl" />
          <div className="absolute top-1/2 right-1/3 w-[250px] h-[250px] rounded-full bg-pink-200/20 dark:bg-pink-900/10 blur-3xl" />
        </div>

        <div className="relative flex w-full flex-col items-center">
          <Link to={localePath('/')} aria-label="PixAI">
            <Logo className="h-7 sm:h-8" />
          </Link>

          <h1 className="mt-3 sm:mt-4 lg:mt-5 text-xl sm:text-2xl lg:text-3xl font-bold text-zinc-900 dark:text-white tracking-tight text-center">
            {effectiveIsLogin
              ? t('auth:landing.headline-login', 'Welcome back to PixAI')
              : t('auth:landing.headline', 'Create stunning AI art in seconds')}
          </h1>
          <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm lg:text-base text-zinc-500 dark:text-zinc-400 text-center max-w-md">
            {effectiveIsLogin
              ? t(
                  'auth:landing.subheadline-login',
                  'Sign in to continue creating with your free daily credits.',
                )
              : t(
                  'auth:landing.subheadline',
                  '10,000 free credits daily — no credit card needed. Join millions of creators.',
                )}
          </p>

          {/* Sign form + Mio decoration */}
          <div className="relative mt-4 w-full max-w-[480px] sm:mt-6">
            {/* Mio mascot — desktop only, peeks from bottom-right of card */}
            <img
              src={mioChibiUrl}
              alt=""
              aria-hidden="true"
              className="hidden lg:block absolute -bottom-6 -right-20 h-24 w-auto drop-shadow-md pointer-events-none select-none"
            />
            <div
              ref={formRef}
              // `min-h-[360px]` pins the card to the natural height of the
              // compact `<Sign>` OAuth view so any sub-pixel jitter between
              // the SSR `<FormSkeleton />` and the hydrated `<LazySign />`
              // (different button label widths across locales, font
              // rendering, dpr, motion.div first-frame) doesn't push the
              // Mio mascot or the "Trusted by 14M+ creators" line below
              // when the real form mounts. The skeleton + real form both
              // sit inside the same fixed-height card.
              className="relative bg-white dark:bg-zinc-800 rounded-2xl shadow-xl ring-1 ring-zinc-200 dark:ring-zinc-700 min-h-[360px]"
            >
              <ClientOnly fallback={<FormSkeleton />}>
                {() => (
                  <Suspense fallback={<FormSkeleton />}>
                    <LazySign
                      compact
                      isSignPage
                      onSuccess={() => {
                        navigate(
                          to ||
                            (!effectiveIsLogin && code
                              ? `/confirm-referral?code=${code}`
                              : '/'),
                        )
                      }}
                      setIsLogin={isLogin => {
                        if (isLogin) {
                          // Strip `to` / `code` when switching to login: their
                          // presence forces sign-up mode via `effectiveIsLogin`
                          // (see referral-flow note above), which would
                          // immediately flip the form back to sign-up and
                          // make the tab click appear inert.
                          const sp = new URLSearchParams(location.search)
                          sp.delete('to')
                          sp.delete('code')
                          const qs = sp.toString()
                          navigate(`/login${qs ? `?${qs}` : ''}`, {
                            replace: true,
                          })
                        } else {
                          navigate(`/sign-up${location.search}`, {
                            replace: true,
                          })
                        }
                      }}
                      isLogin={effectiveIsLogin}
                    />
                  </Suspense>
                )}
              </ClientOnly>
            </div>
          </div>

          {/* Sticky CTA — appears when form scrolls out */}
          <ClientOnly>
            {() => (
              <StickyCtaButton
                label={
                  effectiveIsLogin
                    ? t('auth:landing.cta-sticky-login', 'Log In')
                    : t('auth:landing.cta-sticky', 'Sign Up Free')
                }
                onClick={scrollToForm}
              />
            )}
          </ClientOnly>

          <div className="mt-4 flex items-center gap-2">
            <img
              src={mioChibiUrl}
              alt=""
              aria-hidden="true"
              className="h-10 sm:hidden w-auto drop-shadow-sm pointer-events-none select-none"
            />
            <p className="text-xs text-zinc-400 dark:text-zinc-500">
              {t('auth:social-proof', 'Trusted by 14M+ creators')}
            </p>
          </div>
        </div>
      </div>

      {/* ── Below fold ── */}
      <div className="bg-zinc-50/80 dark:bg-zinc-900/80 border-t border-zinc-200/50 dark:border-zinc-800/50">
        <div className="flex flex-col items-center px-5 sm:px-6 py-12 lg:py-16 gap-12 lg:gap-16">
          {/* Artwork showcase */}
          <section className="w-full max-w-lg lg:max-w-2xl">
            <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-3 text-center uppercase tracking-wider">
              {t('auth:landing.showcase-label', 'Created by our community')}
            </p>
            <div className="grid grid-cols-3 gap-2 lg:gap-3">
              {(effectiveIsLogin
                ? SHOWCASE_IMAGES_LOGIN
                : SHOWCASE_IMAGES_SIGNUP
              ).map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`${t('auth:landing.showcase-alt', 'AI generated anime art by PixAI community')} #${i + 1}`}
                  loading="lazy"
                  width={300}
                  height={300}
                  className="w-full aspect-square object-cover rounded-lg hover:scale-105 transition-transform duration-200"
                  onError={e => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              ))}
            </div>
          </section>

          {/* How it works — sign-up only */}
          {!effectiveIsLogin && (
            <section className="w-full max-w-2xl lg:max-w-3xl">
              <h2 className="text-xl font-bold text-zinc-900 dark:text-white text-center mb-8">
                {t(
                  'auth:landing.how-it-works.title',
                  'Start creating in 3 easy steps',
                )}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {(['step1', 'step2', 'step3'] as const).map((step, i) => (
                  <div
                    key={step}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="size-10 rounded-full bg-purple-600 text-white flex items-center justify-center text-lg font-bold mb-3">
                      {i + 1}
                    </div>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {t(`auth:landing.how-it-works.${step}.title`)}
                    </p>
                    <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                      {t(`auth:landing.how-it-works.${step}.description`)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Popular tools — internal links */}
          <section className="w-full max-w-2xl lg:max-w-3xl">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white text-center mb-6">
              {effectiveIsLogin
                ? t(
                    'auth:landing.tools.title-login',
                    'Jump back into your tools',
                  )
                : t(
                    'auth:landing.tools.title',
                    "Explore PixAI's creative tools",
                  )}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TOOLS.map(({ key, href }) => (
                <Link
                  key={key}
                  to={localePath(href)}
                  className="flex items-center justify-center px-4 py-3 rounded-xl bg-white dark:bg-zinc-800/80 ring-1 ring-zinc-200/50 dark:ring-zinc-700/50 hover:ring-purple-300 dark:hover:ring-purple-700 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                >
                  {t(`auth:landing.tools.${key}`)}
                </Link>
              ))}
            </div>
          </section>

          {/* Feature details */}
          <section className="w-full max-w-2xl lg:max-w-3xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FEATURES.map(({ icon: Icon, key, href }) => (
                <Link
                  key={key}
                  to={localePath(href)}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-zinc-800/80 ring-1 ring-zinc-200/50 dark:ring-zinc-700/50 hover:ring-purple-300 dark:hover:ring-purple-700 transition-[box-shadow,ring-color] group"
                >
                  <div className="flex-shrink-0 mt-0.5 size-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50 transition-colors">
                    <Icon className="size-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                      {t(`auth:landing.features.${key}.title`)}
                    </p>
                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                      {t(`auth:landing.features.${key}.description`)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* FAQ — SEO long-tail content */}
          <section className="w-full max-w-2xl lg:max-w-3xl">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-white text-center mb-6">
              {t('auth:landing.faq.title', 'Frequently asked questions')}
            </h2>
            <div className="space-y-3">
              {(effectiveIsLogin ? FAQ_KEYS_LOGIN : FAQ_KEYS_SIGNUP).map(
                key => (
                  <details
                    key={key}
                    className="group rounded-xl bg-white dark:bg-zinc-800/80 ring-1 ring-zinc-200/50 dark:ring-zinc-700/50 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-zinc-900 dark:text-zinc-100 select-none">
                      {t(`auth:landing.faq.items.${key}.q`)}
                      <span className="ml-2 text-zinc-400 group-open:rotate-180 transition-transform duration-200">
                        &#9662;
                      </span>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                      {t(`auth:landing.faq.items.${key}.a`)}
                    </div>
                  </details>
                ),
              )}
            </div>
          </section>

          {/* Bottom CTA */}
          <section className="w-full max-w-md text-center">
            <p className="text-lg font-bold text-zinc-900 dark:text-white">
              {effectiveIsLogin
                ? t('auth:landing.headline-login', 'Welcome back to PixAI')
                : t(
                    'auth:landing.headline',
                    'Create stunning AI art in seconds',
                  )}
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              className="mt-4 px-8 py-3 rounded-full bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors cursor-pointer"
            >
              {effectiveIsLogin
                ? t('auth:landing.cta-sticky-login', 'Log In')
                : t('auth:landing.cta-sticky', 'Sign Up Free')}
            </button>
          </section>
        </div>
      </div>
    </main>
  )
}

export const Component = SignPage
